import type { StafferPetRarity } from "@/types";

const categoryIds = [
  "PETS",
  "COLORS",
  "TOPPERS",
  "ACCESSORIES",
  "CUSTOM",
  "INCENTIVES",
] as const;
const isPurchasingEnabled = true;
// Display-only: the server owns the token math (floor(totalCoinsEarned / 550)).
const coinsPerIncentiveToken = 550;
const incentiveTokenCost = 1;

const sections = {
  // Free pets come from stafferPets.AVAILABLE_SPECIES; priced pets live here.
  PETS: {
    label: "Pets",
    title: "Pet Pen",
    description: "Unlock new pets · swap owned pets anytime",
    items: [
      { id: "BAT", price: 550, rarity: "EPIC" },
      { id: "PHOENIX", price: 0, rarity: "LEGENDARY", isExclusive: true, description: "Exclusive unlock" },
    ],
  },
  COLORS: {
    label: "Colors",
    title: "Color Vault",
    description: "Unlock once, wear on any pet",
    items: [
      {
        id: "RAINBOW",
        price: 0,
        isExclusive: true,
        description: "Exclusive unlock · shifting rainbow",
      },
      { id: "AURORA", price: 100 },
      { id: "ROSE", price: 100 },
      { id: "BRONZE", price: 100 },
      { id: "NEBULA", price: 250 },
      { id: "SILVER", price: 250 },
      { id: "GOLD", price: 400 },
      {
        id: "PHX_RED",
        description: "Exclusive · 14-day streak reward",
        isExclusive: true,
        price: 0,
      },
      {
        id: "CENTRE_COURT",
        description: "Exclusive custom color",
        isExclusive: true,
        price: 0,
      },
      {
        id: "CHAMPIONSHIP_WHITE",
        description: "Exclusive custom color",
        isExclusive: true,
        price: 0,
      },
    ],
  },
  TOPPERS: {
    label: "Toppers",
    title: "Topper Rack",
    description: "Unlock once, wear on any pet",
    items: [
      { id: "BAT_PERCH", price: 50, rarity: "COMMON" },
      {
        id: "LEIA_BUNS",
        description: "Retired limited edition",
        isRetired: true,
        price: 50,
        rarity: "UNCOMMON",
      },
      { id: "HARD_HAT", price: 80, rarity: "UNCOMMON" },
      { id: "HOCKEY_MASK", price: 100 },
      { id: "LABORUP_CAMO_HAT", price: 150 },
      {
        id: "STORMTROOPER_HELMET",
        description: "Retired limited edition",
        isRetired: true,
        price: 100,
        rarity: "RARE",
      },
      { id: "COWBOY_HAT", price: 150, rarity: "RARE" },
      { id: "BEANS_HAT", price: 200, rarity: "EPIC" },
      { id: "WITCH_HAT", price: 100, rarity: "RARE" },
      {
        id: "VADER_HELMET",
        description: "Retired limited edition",
        isRetired: true,
        price: 200,
        rarity: "LEGENDARY",
      },
    ],
  },
  ACCESSORIES: {
    label: "Accessories",
    title: "Accessory Case",
    description: "Equip alongside your topper",
    items: [
      { id: "BLOOM_GLACIER_CRUSH", price: 50, rarity: "COMMON" },
      { id: "CANDY_BUCKET", price: 50, rarity: "COMMON" },
      { id: "FLOATING_GHOST", price: 50, rarity: "COMMON" },
      { id: "BLUE_LIGHTSABER", isRetired: true, price: 50, rarity: "UNCOMMON" },
      {
        id: "GREEN_LIGHTSABER",
        isRetired: true,
        price: 50,
        rarity: "UNCOMMON",
      },
      { id: "RED_LIGHTSABER", isRetired: true, price: 50, rarity: "UNCOMMON" },
      {
        id: "TENNIS_RACKET",
        description: "Exclusive custom accessory",
        isExclusive: true,
        price: 0,
        rarity: "EPIC",
      },
      { id: "WITCH_BROOM", price: 100, rarity: "RARE" },
      { id: "WHITE_MONSTER_CAN", price: 50, rarity: "COMMON" },
      {
        id: "MANDALORIAN_JETPACK",
        isRetired: true,
        price: 100,
        rarity: "RARE",
      },
      { id: "SITH_LIGHTNING", isRetired: true, price: 150, rarity: "EPIC" },
      {
        id: "FIRE_BREATH",
        description: "Exclusive · 14-day streak reward",
        isExclusive: true,
        price: 0,
        rarity: "LEGENDARY",
      },
    ],
  },
  CUSTOM: {
    label: "Custom",
    title: "Custom Lab",
    description: "One-of-one creations made for you",
    items: [
      {
        id: "CUSTOM_TOPPER_ACCESSORY_COMMISSION",
        description: "Choose one custom topper or accessory made for you.",
        icon: "ACCESSORY",
        price: 275,
        title: "Custom Topper / Accessory",
      },
      {
        id: "CUSTOM_COLOR_COMMISSION",
        description: "A one-of-one palette built around your idea.",
        icon: "COLOR",
        price: 350,
        title: "Custom Color",
      },
      {
        id: "CUSTOM_LEADERBOARD_TAKEOVER_COMMISSION",
        description:
          "A one-of-one leaderboard takeover animation made for everyone win you take the lead in staffer mobile.",
        icon: "LEADERBOARD",
        price: 350,
        title: "Custom Leaderboard Takeover",
      },
      {
        id: "CUSTOM_PET_COMMISSION",
        description: "A brand-new pet created just for you.",
        icon: "PET",
        price: 550,
        title: "Custom Pet",
      },
      {
        id: "CUSTOM_EVOLUTION_COMMISSION",
        description:
          "A custom evolved form made for one of your pets. Your pet will evolve if you're leading in revenue/submits on the week or if you're the reigning revenue leader from the past month.",
        icon: "EVOLUTION",
        price: 550,
        title: "Custom Evolution",
      },
    ],
  },
  INCENTIVES: {
    label: "Incentives",
    title: "Incentives",
    description: "Each costs 1 token · redeemable again and again",
    // `icon` maps to a lucide icon in StafferPetShopWizard's incentiveIcons.
    items: [
      {
        id: "FOUR_WORKOUT_CLASSES",
        colorClassName: "bg-emerald-300",
        description: "Choose four workout classes and we'll cover them.",
        icon: "WORKOUT",
        title: "4 Workout Classes",
      },
      {
        id: "DATE_NIGHT_FOOD_GIFT_CARD",
        colorClassName: "bg-rose-300",
        description: "A food gift card for your next date night.",
        icon: "FOOD",
        title: "Date Night Gift Card",
      },
      {
        id: "MASSAGE",
        colorClassName: "bg-sky-300",
        description: "Book a massage on us and unwind.",
        icon: "MASSAGE",
        title: "Massage",
      },
    ],
  },
} as const;

export const stafferPetShop = {
  categoryIds,
  coinsPerIncentiveToken,
  isItemAvailable: (item: unknown): boolean => {
    if (typeof item !== "object" || item === null) return true;
    if ("isRetired" in item && item.isRetired === true) return false;
    if (!("availableUntil" in item)) return true;
    if (typeof item.availableUntil !== "string") return true;
    return Date.now() < new Date(item.availableUntil).getTime();
  },
  getPriceLabel: (price: number): string => price.toString(),
  // Items without a tier (free pets, one-off custom unlocks) fall back here.
  getItemRarity: (
    item: unknown,
    fallback: StafferPetRarity = "COMMON",
  ): StafferPetRarity => {
    if (typeof item !== "object" || item === null) return fallback;
    if (!("rarity" in item) || typeof item.rarity !== "string") return fallback;
    return item.rarity as StafferPetRarity;
  },
  incentiveTokenCost,
  isPurchasingEnabled,
  sections,
};
